import React, { useState, useEffect } from 'react';
import { getRequests, updateRequestStatus, resetEditDeadline, TestRequest } from '../../services/requestService';
import { getActiveTestPackageById, TestPackage } from '../../services/testPackageService';
import { getActiveInsuranceById, Insurance } from '../../services/insuranceService';
import { getActiveSupplementaryInsuranceById, SupplementaryInsurance } from '../../services/supplementaryInsuranceService';
import commandService, { Command, CommandsResponse } from '../../services/commandService';
import socketService from '../../services/socketService';
import { toast } from 'react-toastify';
import API_CONFIG from '../../config/api';
import Select from 'react-select';
import FormattedNumberInput from '../../components/ui/FormattedNumberInput';

// RequestDetailsModal component
interface DetailedTestRequest extends TestRequest {
  patientInfo?: {
    patientType: string;
    firstName: string;
    lastName: string;
    nationalId: string;
    mobile: string;
    gender: string;
  };
  samplingLocation?: {
    type: 'home' | 'lab';
    needsContainer?: boolean;
    freeTransportation?: boolean;
  };
  addressInfo?: {
    region: string;
    field: string;
    street: string;
    alley?: string;
    building?: string;
    unit?: string;
    number?: string;
    coordinates?: [number, number];
  };
  importantNote?: string;
  ageInfo?: {
    age: number;
  };
  insuranceInfo?: {
    type: string;
    nationalIdImageUrl?: string;
    testListImageUrl?: string;
    prescriptionImageUrl?: string;
    insuranceBookletImageUrl?: string;
    trackingCode?: string;
    description?: string;
    basicInsurance?: string;
    supplementaryInsurance?: string;
    selectedPackage?: string;
  };
  rejectionReason?: string;
  instructions?: string;
  editDeadline?: string;
  editVersion?: number;
  labResponseDateTime?: string; // Added for lab response datetime
  continueDateTime?: string;   // Added for continuation datetime
  paymentDateTime?: string;    // Added for payment datetime
  amount?: number;             // Amount to be paid (in Toman)
  paymentExtensionRequest?: boolean; // Payment extension request
  paymentExtensionStatus?: 'pending' | 'approved' | 'rejected'; // Payment extension status
  paymentExtensionRejectionReason?: string; // Payment extension rejection reason
  samplingDate?: string;       // Sampling date
  samplingTime?: string;       // Sampling time
  statusLogs?: Array<{
    timestamp: string;
    changedBy: {
      userId: string;
      userName: string;
      userType: 'admin' | 'user';
    };
    action: string;
    oldStatus?: string;
    newStatus?: string;
    details?: string;
  }>;
}

// Helper function to translate status
const translateStatus = (status: string, request?: TestRequest): string => {
  if (status === 'paid') {
    // Check if total amount (base + transportation) is 0 OR no transaction exists
    if (request) {
      const baseAmount = request.amount || 0;
      const transportationAmount = request.transportationPrice || 0;
      const totalAmount = baseAmount + transportationAmount;
      
      if (totalAmount === 0 || !request.transaction) {
        return 'نهایی شده';
      }
    }
    return 'نهایی و پرداخت شده';
  }
  
  const statusMap: Record<string, string> = {
    incomplete: 'ناتمام',
    canceled: 'لغو شده',
    reviewing: 'در حال بررسی',
    approved: 'تایید شده',
    rejected: 'تایید نشده',
    edited: 'ویرایش شده',
    expired: 'منقضی شده',
  };
  return statusMap[status] || status;
};

// Helper function to translate patient type to Persian
const translatePatientType = (type: string): string => {
  const typeMap: Record<string, string> = {
    self: 'خودم',
    child: 'فرزند',
    spouse: 'همسر',
    father: 'پدر',
    mother: 'مادر',
    parent: 'والدین',
    other: 'سایر'
  };
  return typeMap[type] || type;
};

// Helper function to get status color and text
const getStatusInfo = (status: string, request?: TestRequest): { color: string; text: string } => {
  switch (status) {
    case 'approved': return { color: 'bg-green-100 text-green-800', text: translateStatus(status, request) };
    case 'canceled': return { color: 'bg-gray-100 text-gray-800', text: translateStatus(status, request) };
    case 'expired': return { color: 'bg-orange-100 text-orange-800', text: translateStatus(status, request) };
    case 'incomplete': return { color: 'bg-purple-100 text-purple-800', text: translateStatus(status, request) };
    case 'paid':
      return { color: 'bg-green-100 text-green-800', text: translateStatus(status, request) };
    case 'rejected': return { color: 'bg-red-100 text-red-800', text: translateStatus(status, request) };
    default: return { color: 'bg-gray-100 text-gray-800', text: translateStatus(status, request) };
  }
};

// Badge component for tab counts
const TabBadge: React.FC<{ count: number }> = ({ count }) => {
  if (count === 0) return null;
  
  return (
    <span className="mr-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
      {count > 99 ? '99+' : count}
    </span>
  );
};

// Build insurance type display name
const getInsuranceTypeDisplay = (type: string) => {
  switch (type) {
    case 'free': return 'آزاد';
    case 'paper': return 'دفترچه بیمه';
    case 'electronic': return 'نسخه الکترونیک';
    case 'package': return 'پکیج آزمایشگاه';
    default: return type;
  }
};

// Get basic insurance display name (using real insurance data)
const getBasicInsuranceDisplay = (insuranceId: string | undefined, selectedInsurance: Insurance | null) => {
  if (!insuranceId) return '';
  if (insuranceId === 'none') return 'هیچکدام';
  
  // Use the fetched insurance data if available
  if (selectedInsurance && selectedInsurance._id === insuranceId) {
    return selectedInsurance.name;
  }
  
  // If insurance data not available, show "حذف شده" (deleted)
  return 'حذف شده';
};

// Get supplementary insurance display name (using real supplementary insurance data)
const getSupplementaryInsuranceDisplay = (supplementaryInsurance: string | undefined, selectedSupplementaryInsurance: SupplementaryInsurance | null) => {
  if (!supplementaryInsurance) return '';
  if (supplementaryInsurance === 'none') return 'هیچکدام';
  
  if (selectedSupplementaryInsurance && selectedSupplementaryInsurance._id === supplementaryInsurance) {
    return selectedSupplementaryInsurance.name;
  }
  
  // If insurance data not available, show "حذف شده" (deleted)
  return 'حذف شده';
};

// Calculate remaining edit time in hours
const calculateRemainingEditTime = (editDeadline: string | undefined): number => {
  if (!editDeadline) return 0;
  
  const deadline = new Date(editDeadline);
  const now = new Date();
  const diffMs = deadline.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  
  return Math.max(0, diffHours);
};

// Format remaining edit time
const formatRemainingEditTime = (editDeadline: string | undefined): string => {
  if (!editDeadline) return '';
  
  const deadline = new Date(editDeadline);
  const now = new Date();
  const diffMs = deadline.getTime() - now.getTime();
  
  if (diffMs <= 0) return 'منقضی شده';
  
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (diffHours > 0) {
    return `${diffHours} ساعت و ${diffMinutes} دقیقه`;
  } else {
    return `${diffMinutes} دقیقه`;
  }
};

// Check if edit deadline has passed
const isEditDeadlinePassed = (editDeadline: string | undefined): boolean => {
  if (!editDeadline) return false;
  
  const deadline = new Date(editDeadline);
  const now = new Date();
  return now.getTime() > deadline.getTime();
};

interface RequestDetailsProps {
  requestId: string | null;
  onClose: () => void;
  onStatusChange?: (requestId: string, status: string, reason?: string, instructions?: string, samplingLocation?: string, needsContainer?: boolean, freeTransportation?: boolean, amount?: number) => Promise<void>;
  showActionButtons?: boolean;
}

// RequestDetails component for inline display
const RequestDetails: React.FC<RequestDetailsProps> = ({ requestId, onClose, onStatusChange, showActionButtons = false }) => {
  const [requestDetails, setRequestDetails] = useState<DetailedTestRequest | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<TestPackage | null>(null);
  const [selectedInsurance, setSelectedInsurance] = useState<Insurance | null>(null);
  const [selectedSupplementaryInsurance, setSelectedSupplementaryInsurance] = useState<SupplementaryInsurance | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState<string | null>(null);
  const [approvingExtension, setApprovingExtension] = useState(false);
  const [rejectingExtension, setRejectingExtension] = useState(false);

  useEffect(() => {
    if (!requestId) return;

    const fetchRequestDetails = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('لطفا ابتدا وارد حساب کاربری خود شوید.');
          setLoading(false);
          return;
        }

        const response = await fetch(API_CONFIG.getUrl(`admin/requests/${requestId}`), {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          let errorMessage = 'خطا در دریافت اطلاعات درخواست';
          try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const errorData = await response.json();
              errorMessage = errorData.message || errorMessage;
            } else {
              errorMessage = `خطای سرور: ${response.status} ${response.statusText}`;
            }
          } catch (e) {

          }
          throw new Error(errorMessage);
        }

        const data = await response.json();
        setRequestDetails(data.data);
        
        // Fetch package details if available
        if (data.data?.insuranceInfo?.selectedPackage) {
          try {
            const packageDetails = await getActiveTestPackageById(data.data.insuranceInfo.selectedPackage);
            setSelectedPackage(packageDetails);
          } catch (packageError) {

          }
        }
        
        // Fetch insurance details if available
        if (data.data?.insuranceInfo?.basicInsurance) {
          try {
            const insuranceDetails = await getActiveInsuranceById(data.data.insuranceInfo.basicInsurance);
            setSelectedInsurance(insuranceDetails);
          } catch (insuranceError) {

          }
        }

        // Fetch supplementary insurance details if available
        if (data.data?.insuranceInfo?.supplementaryInsurance && data.data.insuranceInfo.supplementaryInsurance !== 'none') {
          try {
            const supplementaryInsuranceDetails = await getActiveSupplementaryInsuranceById(data.data.insuranceInfo.supplementaryInsurance);
            setSelectedSupplementaryInsurance(supplementaryInsuranceDetails);
          } catch (supplementaryInsuranceError) {

          }
        }
      } catch (err: any) {
        setError(err.message || 'یک خطای ناشناخته رخ داد.');

      } finally {
        setLoading(false);
      }
    };

    fetchRequestDetails();
  }, [requestId]);

  // Handle approve request
  const handleApproveRequest = async (instructions?: string, samplingLocation?: string, needsContainer?: boolean, freeTransportation?: boolean, amount?: number) => {
    if (!requestId || !onStatusChange) return;
    
    try {
      await onStatusChange(requestId, 'approved', undefined, instructions, samplingLocation, needsContainer, freeTransportation, amount);
      // Refresh request details after status change
      const fetchUpdatedDetails = async () => {
        try {
          const token = localStorage.getItem('authToken');
          if (!token) return;

          const response = await fetch(API_CONFIG.getUrl(`admin/requests/${requestId}`), {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            setRequestDetails(data.data);
          }
        } catch (error) {

        }
      };
      
      fetchUpdatedDetails();
      setApproveModalOpen(false);
      toast.success('درخواست با موفقیت تایید شد');
    } catch (error) {

      toast.error('خطا در تایید درخواست');
    }
  };

  // Handle reject request
  const handleRejectRequest = async (reason: string) => {
    if (!requestId || !onStatusChange) return;
    
    try {
      await onStatusChange(requestId, 'rejected', reason);
      // Refresh request details after status change
      const fetchUpdatedDetails = async () => {
        try {
          const token = localStorage.getItem('authToken');
          if (!token) return;

          const response = await fetch(API_CONFIG.getUrl(`admin/requests/${requestId}`), {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            setRequestDetails(data.data);
          }
        } catch (error) {

        }
      };
      
      fetchUpdatedDetails();
      setRejectModalOpen(false);
      toast.success('درخواست با موفقیت رد شد');
    } catch (error) {

      toast.error('خطا در رد درخواست');
    }
  };

  // Handle approve payment extension
  const handleApprovePaymentExtension = async () => {
    if (!requestId) return;
    
    try {
      setApprovingExtension(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(API_CONFIG.getUrl(`admin/requests/${requestId}/approve-payment-extension`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Refresh request details after status change
          const fetchUpdatedDetails = async () => {
            try {
              const response = await fetch(API_CONFIG.getUrl(`admin/requests/${requestId}`), {
                headers: {
                  'Authorization': `Bearer ${token}`,
                },
              });

              if (response.ok) {
                const data = await response.json();
                setRequestDetails(data.data);
              }
            } catch (error) {

            }
          };
          
          fetchUpdatedDetails();
          toast.success('درخواست تمدید پرداخت با موفقیت تایید شد');
        } else {
          toast.error(data.message || 'خطا در تایید درخواست تمدید پرداخت');
        }
      } else {
        toast.error('خطا در ارتباط با سرور');
      }
    } catch (error) {

      toast.error('خطا در تایید درخواست تمدید پرداخت');
    } finally {
      setApprovingExtension(false);
    }
  };

  // Handle reject payment extension
  const handleRejectPaymentExtension = async () => {
    if (!requestId) return;
    
    const rejectionReason = prompt('دلیل عدم تایید درخواست تمدید پرداخت را وارد کنید (اختیاری):');
    
    // User canceled the prompt
    if (rejectionReason === null) return;
    
    try {
      setRejectingExtension(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(API_CONFIG.getUrl(`admin/requests/${requestId}/reject-payment-extension`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rejectionReason: rejectionReason.trim() || undefined
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Refresh request details after status change
          const fetchUpdatedDetails = async () => {
            try {
              const response = await fetch(API_CONFIG.getUrl(`admin/requests/${requestId}`), {
                headers: {
                  'Authorization': `Bearer ${token}`,
                },
              });

              if (response.ok) {
                const data = await response.json();
                setRequestDetails(data.data);
              }
            } catch (error) {

            }
          };
          
          fetchUpdatedDetails();
          toast.success('درخواست تمدید پرداخت رد شد و درخواست لغو شده است. پیام به کاربر ارسال شد.');
        } else {
          toast.error(data.message || 'خطا در رد درخواست تمدید پرداخت');
        }
      } else {
        toast.error('خطا در ارتباط با سرور');
      }
    } catch (error) {

      toast.error('خطا در رد درخواست تمدید پرداخت');
    } finally {
      setRejectingExtension(false);
    }
  };

  // Helper function to render uploaded image preview
  const renderImagePreview = (imageUrl: string | undefined, title: string) => {
    if (!imageUrl) return null;
    return (
      <div className="mb-4">
        <h5 className="font-medium text-sm mb-2">{title}</h5>
        <div className="border border-slate-300 rounded-sm p-2 bg-slate-50">
          <img
            src={`${API_CONFIG.uploadURL}${imageUrl}`}
            alt={title}
            className="max-h-40 object-contain mx-auto cursor-pointer"
            onClick={() => {
              setModalImage(`${API_CONFIG.uploadURL}${imageUrl}`);
              setImageModalOpen(true);
            }}
          />
        </div>
      </div>
    );
  };

  if (!requestId) return null;

  // We'll use the renderImagePreview function passed from the parent component

  return (
    <div>
      {/* Details content */}
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-2"></div>
          <p>در حال بارگذاری...</p>
        </div>
      ) : error ? (
        <div className="text-center text-red-500">{error}</div>
      ) : !requestDetails ? (
        <div className="text-center text-gray-500 py-8">جزئیات درخواست یافت نشد</div>
      ) : (
        <div>
          {/* Status Display */}
          <div className="mb-6 p-4 rounded-md bg-blue-50 border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-700">
              وضعیت فعلی درخواست: {translateStatus(requestDetails.status, requestDetails)}
              {requestDetails.editVersion && requestDetails.editVersion > 0 && (
                <span className="mr-2 text-sm bg-blue-200 text-blue-800 px-2 py-1 rounded">
                  ویرایش {requestDetails.editVersion}
                </span>
              )}
            </h3>
          </div>

          {/* Important Note Display - Show if important note exists */}
          {requestDetails.importantNote && (
            <div className="mb-6 p-4 rounded-md bg-red-50 border border-red-300">
              <h3 className="text-lg font-bold text-red-700 mb-2">
                <span className="ml-2">⚠️</span>
                نکته مهم:
              </h3>
              <div className="text-red-800 bg-white p-3 rounded border border-red-300">
                <p className="font-bold text-red-700">
                  {requestDetails.importantNote}
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons - Only shown when showActionButtons is true and status is reviewing or edited */}
          {showActionButtons && (requestDetails.status === 'reviewing' || requestDetails.status === 'edited') && (
            <div className="mb-6 p-4 rounded-md bg-gray-50 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">اقدامات مدیریتی</h3>
              {requestDetails.status === 'edited' && (
                <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                  <span className="font-medium">📝 این درخواست ویرایش شده است</span>
                  {requestDetails.editVersion && requestDetails.editVersion > 0 && (
                    <span className="mr-2">- نسخه ویرایش: {requestDetails.editVersion}</span>
                  )}
                </div>
              )}
              <div className="flex space-x-4 space-x-reverse gap-2">
                <button
                  onClick={() => setApproveModalOpen(true)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors"
                >
                  تایید درخواست
                </button>
              
                <button
                  onClick={() => setRejectModalOpen(true)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium transition-colors"
                >
                  رد درخواست
                </button>
              </div>
            </div>
          )}

          {/* Payment Extension Request Display - Show if there was ever a payment extension request */}
          {requestDetails.paymentExtensionStatus && (
            <div className={`mb-6 p-4 rounded-md border ${
              requestDetails.paymentExtensionStatus === 'approved' 
                ? 'bg-green-50 border-green-200' 
                : requestDetails.paymentExtensionStatus === 'rejected'
                ? 'bg-red-50 border-red-200'
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <h3 className={`text-lg font-semibold mb-2 ${
                requestDetails.paymentExtensionStatus === 'approved' 
                  ? 'text-green-700' 
                  : requestDetails.paymentExtensionStatus === 'rejected'
                  ? 'text-red-700'
                  : 'text-yellow-700'
              }`}>
                <span className="ml-2">
                  {requestDetails.paymentExtensionStatus === 'approved' 
                    ? '✅' 
                    : requestDetails.paymentExtensionStatus === 'rejected'
                    ? '❌'
                    : '⏰'
                  }
                </span>
                درخواست تمدید پرداخت
              </h3>
              <div className={`bg-white p-3 rounded border ${
                requestDetails.paymentExtensionStatus === 'approved' 
                  ? 'text-green-700 border-green-200' 
                  : requestDetails.paymentExtensionStatus === 'rejected'
                  ? 'text-red-700 border-red-200'
                  : 'text-yellow-700 border-yellow-200'
              }`}>
                {requestDetails.paymentExtensionStatus === 'approved' ? (
                  <p className="font-medium">
                    ✅ درخواست تمدید پرداخت تایید شده است.
                  </p>
                ) : requestDetails.paymentExtensionStatus === 'rejected' ? (
                  <>
                    <p className="mb-2 font-medium">
                      ❌ درخواست تمدید پرداخت رد شده است.
                    </p>
                    {requestDetails.paymentExtensionRejectionReason && (
                      <div className="mt-2 p-2 bg-red-100 rounded border border-red-300">
                        <strong className="text-sm">دلیل رد:</strong>
                        <p className="text-sm mt-1">{requestDetails.paymentExtensionRejectionReason}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="font-medium">
                    ⏳ کاربر درخواست تمدید مهلت پرداخت کرده است.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Rejection Reason Display - Show if rejection reason exists (regardless of current status) */}
          {requestDetails.rejectionReason && (
            <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200">
              <h4 className="text-sm font-semibold text-red-700 mb-2">
                {requestDetails.status === 'rejected' ? 'دلایل رد درخواست:' : 'دلایل رد قبلی:'}
              </h4>
              <div className="text-red-600 bg-white p-2 rounded border border-red-200 text-sm">
                <div className="space-y-1">
                  {requestDetails.rejectionReason.split('\n').map((line, index) => (
                    <div key={index} className="flex items-start">
                      <span className="text-red-500 ml-2">{line.trim().startsWith('•') ? '' : '•'}</span>
                      <span>{line.replace(/^•\s*/, '')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Edit Deadline Display - Only shown when status is rejected and edit deadline exists */}
          {requestDetails.status === 'rejected' && requestDetails.editDeadline && (
            <div className="mb-6 p-4 rounded-md bg-yellow-50 border border-yellow-200">
              <h3 className="text-lg font-semibold text-yellow-700 mb-2">
                <span className="ml-2">⏰</span>
                فرصت ویرایش درخواست
              </h3>
              <div className="text-yellow-700 bg-white p-3 rounded border border-yellow-200">
                {!isEditDeadlinePassed(requestDetails.editDeadline) ? (
                  <div>
                    <p className="mb-2">
                      <strong>زمان باقی‌مانده:</strong> {formatRemainingEditTime(requestDetails.editDeadline)}
                    </p>
                    <p className="text-sm text-yellow-600">
                      کاربر تا {new Intl.DateTimeFormat('fa-IR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }).format(new Date(requestDetails.editDeadline))} فرصت ویرایش درخواست را دارد.
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-red-600 font-medium mb-2">
                      <span className="ml-1">❌</span>
                      زمان ویرایش منقضی شده
                    </p>
                    <p className="text-sm text-gray-600">
                      مهلت ویرایش در {new Intl.DateTimeFormat('fa-IR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }).format(new Date(requestDetails.editDeadline))} به پایان رسیده است.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Instructions Display - Show if instructions exist (regardless of current status) */}
          {requestDetails.instructions && (
            <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200">
              <h4 className="text-sm font-semibold text-green-700 mb-2">
                <span className="ml-2">📋</span>
                {requestDetails.status === 'approved' ? 'دستورالعمل‌ها:' : 'دستورالعمل‌های قبلی:'}
              </h4>
              <div className="text-green-700 bg-white p-2 rounded border border-green-200 text-sm">
                <div className="space-y-1">
                  {requestDetails.instructions.split('\n').map((line, index) => (
                    <div key={index} className="flex items-start">
                      <span className="text-green-600 ml-2">{line.trim().startsWith('•') ? '' : '•'}</span>
                      <span>{line.replace(/^•\s*/, '')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Amount Display - Show if amount exists (regardless of status) */}
          {requestDetails.amount && (
            <div className="mb-4 p-3 rounded-md bg-blue-50 border border-blue-200">
              <h4 className="text-sm font-semibold text-blue-700 mb-2">
                <span className="ml-2">💰</span>
                {requestDetails.status === 'approved' ? 'هزینه قابل پرداخت:' : 'هزینه تعیین شده:'}
              </h4>
              <div className="text-blue-700 bg-white p-2 rounded border border-blue-200 text-sm">
                <div className="text-lg font-bold text-blue-800">
                  {requestDetails.amount === 0 ? 'صفر (رایگان)' : `${new Intl.NumberFormat('fa-IR').format(requestDetails.amount)} تومان`}
                </div>
              </div>
            </div>
          )}

          {/* User Info Section */}
          {requestDetails.userId && (
            <div className="mb-8 border-b border-slate-200 pb-6">
              <h4 className="font-bold text-slate-700 mb-4 text-lg">مشخصات کاربر</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div><span className="font-medium text-slate-600">نام و نام خانوادگی:</span> {`${requestDetails.userId.name || ''} ${requestDetails.userId.lastName || ''}`}</div>
                <div><span className="font-medium text-slate-600">کد ملی:</span> {requestDetails.userId.nationalId || '-'}</div>
                <div><span className="font-medium text-slate-600">شماره موبایل:</span> {requestDetails.userId.mobile || '-'}</div>
              </div>
            </div>
          )}

          {/* Patient Info Section */}
          {requestDetails.patientInfo && (
            <div className="mb-8 border-b border-slate-200 pb-6">
              <h4 className="font-bold text-slate-700 mb-4 text-lg">مشخصات بیمار</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div><span className="font-medium text-slate-600">نام و نام خانوادگی:</span> {`${requestDetails.patientInfo.firstName} ${requestDetails.patientInfo.lastName}`}</div>
                <div><span className="font-medium text-slate-600">کد ملی:</span> {requestDetails.patientInfo.nationalId}</div>
                <div><span className="font-medium text-slate-600">شماره موبایل:</span> {requestDetails.patientInfo.mobile}</div>
                <div><span className="font-medium text-slate-600">جنسیت:</span> {requestDetails.patientInfo.gender === 'male' ? 'مرد' : 'زن'}</div>
                <div><span className="font-medium text-slate-600">نسبت بیمار:</span> {translatePatientType(requestDetails.patientInfo.patientType)}</div>
                {requestDetails.ageInfo && (
                  <div><span className="font-medium text-slate-600">سن:</span> {requestDetails.ageInfo.age} سال</div>
                )}
              </div>
            </div>
          )}

          {/* Sampling Location Section */}
          <div className="mb-8 border-b border-slate-200 pb-6">
            <h4 className="font-bold text-slate-700 mb-4 text-lg">محل نمونه گیری</h4>
            <div className="text-sm">
              <p><span className="font-medium text-slate-600">نوع نمونه گیری:</span> 
                {requestDetails.samplingLocation?.type === 'lab' ? ' نمونه گیری در آزمایشگاه' : ' نمونه گیری در منزل'}
              </p>
              
              {requestDetails.samplingLocation?.type === 'home' && (
                <>
                  {requestDetails.addressInfo && (
                <>
                  <p className="mt-2"><span className="font-medium text-slate-600">منطقه:</span> {requestDetails.addressInfo.region}</p>
                  <p>
                    {requestDetails.addressInfo.field && `میدان ${requestDetails.addressInfo.field}، `}
                    {requestDetails.addressInfo.street && `خیابان ${requestDetails.addressInfo.street}، `}
                    {requestDetails.addressInfo.alley && `کوچه ${requestDetails.addressInfo.alley}، `}
                    {requestDetails.addressInfo.building && `ساختمان ${requestDetails.addressInfo.building}، `}
                    {requestDetails.addressInfo.unit && `واحد ${requestDetails.addressInfo.unit}، `}
                    {requestDetails.addressInfo.number && `پلاک ${requestDetails.addressInfo.number}`}
                  </p>
                    </>
                  )}
                  
                  {/* Home Sampling Options */}
                  <div className="mt-3 space-y-1">
                    <div className="flex items-center">
                      <span className="font-medium text-slate-600 ml-2">نیاز به ظرف:</span>
                      {requestDetails.samplingLocation.needsContainer ? (
                        <span className="text-green-600 font-medium">✓ دارد</span>
                      ) : (
                        <span className="text-red-600 font-medium">✗ ندارد</span>
                      )}
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium text-slate-600 ml-2">ایاب و ذهاب:</span>
                      {requestDetails.samplingLocation.freeTransportation ? (
                        <span className="text-green-600 font-medium">✓ رایگان</span>
                      ) : (
                        <span className="text-red-600 font-medium">✗ با هزینه</span>
                      )}
                    </div>
                  </div>
                </>
              )}
              
              {requestDetails.samplingLocation?.type === 'lab' && (
                <p className="mt-2 text-blue-600">
                  <span className="font-medium">📍</span> بیمار باید به آزمایشگاه مراجعه کند
                </p>
              )}
            </div>
          </div>

          {/* Insurance Info Section */}
          {requestDetails.insuranceInfo && (
            <div className="mb-8">
              <h4 className="font-bold text-slate-700 mb-4 text-lg">اطلاعات بیمه و آزمایش</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm mb-4">
                <div><span className="font-medium text-slate-600">نوع درخواست:</span> {getInsuranceTypeDisplay(requestDetails.insuranceInfo.type)}</div>

                {requestDetails.insuranceInfo.type === 'paper' && (
                  <>
                    <div><span className="font-medium text-slate-600">بیمه پایه:</span> {getBasicInsuranceDisplay(requestDetails.insuranceInfo.basicInsurance, selectedInsurance)}</div>
                    {requestDetails.insuranceInfo.supplementaryInsurance && (
                      <div><span className="font-medium text-slate-600">بیمه تکمیلی:</span> {getSupplementaryInsuranceDisplay(requestDetails.insuranceInfo.supplementaryInsurance, selectedSupplementaryInsurance)}</div>
                    )}
                  </>
                )}

                {requestDetails.insuranceInfo.type === 'electronic' && (
                  <>
                    <div><span className="font-medium text-slate-600">بیمه پایه:</span> {getBasicInsuranceDisplay(requestDetails.insuranceInfo.basicInsurance, selectedInsurance)}</div>
                    {requestDetails.insuranceInfo.supplementaryInsurance && (
                      <div><span className="font-medium text-slate-600">بیمه تکمیلی:</span> {getSupplementaryInsuranceDisplay(requestDetails.insuranceInfo.supplementaryInsurance, selectedSupplementaryInsurance)}</div>
                    )}
                    <div><span className="font-medium text-slate-600">کد رهگیری نسخه:</span> {requestDetails.insuranceInfo.trackingCode}</div>
                  </>
                )}

                {requestDetails.insuranceInfo.type === 'package' && selectedPackage && (
                  <div className="md:col-span-2">
                    <span className="font-medium text-slate-600">پکیج انتخاب شده:</span>
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-sm">
                      <h5 className="font-medium text-blue-800">{selectedPackage.name}</h5>
                      {selectedPackage.description && (
                        <p className="text-sm text-blue-600 mt-1">{selectedPackage.description}</p>
                      )}
                      <div className="mt-2 flex items-center gap-2">
                        {selectedPackage.price > selectedPackage.discountPrice && (
                          <span className="text-sm text-gray-500 line-through">
                            {new Intl.NumberFormat('fa-IR').format(selectedPackage.price)} تومان
                          </span>
                        )}
                        <span className="text-sm font-medium text-blue-800">
                          {selectedPackage.discountPrice === 0 
                            ? 'رایگان' 
                            : `${new Intl.NumberFormat('fa-IR').format(selectedPackage.discountPrice)} تومان`
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                {requestDetails.insuranceInfo.type === 'package' && !selectedPackage && (
                  <div className="md:col-span-2">
                    <span className="font-medium text-slate-600">پکیج انتخاب شده:</span>
                    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-yellow-600">⚠️</span>
                        <div>
                          <h5 className="font-medium text-yellow-800">شناسه پکیج: {requestDetails.insuranceInfo.selectedPackage}</h5>
                          <p className="text-sm text-yellow-600 mt-1">
                            جزئیات کامل پکیج در دسترس نیست. ممکن است پکیج حذف شده یا تغییر کرده باشد.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {requestDetails.insuranceInfo.description && (
                <div className="mt-4">
                  <p className="font-medium text-slate-600 mb-1">توضیحات:</p>
                  <p className="bg-slate-50 p-3 rounded-sm border border-slate-200 text-sm">
                    {requestDetails.insuranceInfo.description}
                  </p>
                </div>
              )}

              {/* Uploaded Images */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <h5 className="font-bold text-slate-700 mb-4">تصاویر آپلود شده</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {renderImagePreview(requestDetails.insuranceInfo.nationalIdImageUrl, 'تصویر کارت ملی')}
                  {renderImagePreview(requestDetails.insuranceInfo.testListImageUrl, 'لیست آزمایش‌ها')}
                  {renderImagePreview(requestDetails.insuranceInfo.prescriptionImageUrl, 'تصویر نسخه')}
                  {renderImagePreview(requestDetails.insuranceInfo.insuranceBookletImageUrl, 'تصویر دفترچه بیمه')}
                </div>
                {!(requestDetails.insuranceInfo.nationalIdImageUrl ||
                  requestDetails.insuranceInfo.testListImageUrl ||
                  requestDetails.insuranceInfo.prescriptionImageUrl ||
                  requestDetails.insuranceInfo.insuranceBookletImageUrl) && (
                    <p className="text-sm text-slate-500">هیچ تصویری برای این درخواست آپلود نشده است.</p>
                  )}
              </div>
            </div>
          )}

          {/* Request metadata */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <h5 className="font-bold text-slate-700 mb-4">اطلاعات درخواست</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div><span className="font-medium text-slate-600">شناسه درخواست:</span> {requestDetails.displayId || requestDetails._id.slice(-8)}</div>
              <div><span className="font-medium text-slate-600">تاریخ و ساعت ایجاد درخواست:</span> {new Intl.DateTimeFormat('fa-IR', {year:'numeric', month:'long', day:'numeric', hour:'2-digit', minute:'2-digit'}).format(new Date(requestDetails.createdAt))}</div>
              <div><span className="font-medium text-slate-600">تاریخ و ساعت پاسخ آزمایشگاه:</span> {requestDetails.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR', {year:'numeric', month:'long', day:'numeric', hour:'2-digit', minute:'2-digit'}).format(new Date(requestDetails.labResponseDateTime)) : '-'}</div>
              <div><span className="font-medium text-slate-600">تاریخ و ساعت ویرایش درخواست:</span> {new Intl.DateTimeFormat('fa-IR', {year:'numeric', month:'long', day:'numeric', hour:'2-digit', minute:'2-digit'}).format(new Date(requestDetails.updatedAt))}</div>
              <div><span className="font-medium text-slate-600">تاریخ و ساعت ادامه مراحل درخواست:</span> {requestDetails.continueDateTime ? new Intl.DateTimeFormat('fa-IR', {year:'numeric', month:'long', day:'numeric', hour:'2-digit', minute:'2-digit'}).format(new Date(requestDetails.continueDateTime)) : '-'}</div>
              <div><span className="font-medium text-slate-600">تاریخ و ساعت پرداخت:</span> {requestDetails.paymentDateTime ? new Intl.DateTimeFormat('fa-IR', {year:'numeric', month:'long', day:'numeric', hour:'2-digit', minute:'2-digit'}).format(new Date(requestDetails.paymentDateTime)) : '-'}</div>
              {requestDetails.amount !== undefined && requestDetails.amount !== null && (
                <div><span className="font-medium text-slate-600">قیمت پایه:</span> {requestDetails.amount === 0 ? 'صفر (رایگان)' : `${new Intl.NumberFormat('fa-IR').format(requestDetails.amount)} تومان`}</div>
              )}
              {(requestDetails.status === 'approved' || requestDetails.status === 'paid') && (
                <div><span className="font-medium text-slate-600">ایاب و ذهاب:</span> {requestDetails.samplingLocation?.freeTransportation ? 'رایگان' : 'دارد'}</div>
              )}
            </div>
          </div>

          {/* Logs */}
          {requestDetails.statusLogs && requestDetails.statusLogs.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h5 className="font-bold text-slate-700 mb-4">تاریخچه تغییرات وضعیت</h5>
              <div className="space-y-3">
                {[...requestDetails.statusLogs].reverse().map((log, index) => (
                  <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            log.changedBy.userType === 'admin' 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {log.changedBy.userType === 'admin' ? '👨‍💼 ادمین' : '👤 کاربر'}
                          </span>
                          <span className="text-sm font-medium text-gray-900">{log.changedBy.userName}</span>
                        </div>
                        <div className="text-sm text-gray-700 mb-1">
                          <span className="font-medium">اقدام:</span> {log.details || log.action}
                        </div>
                        {log.oldStatus && log.newStatus && (
                          <div className="text-xs text-gray-600">
                            <span className="font-medium">تغییر وضعیت:</span> {translateStatus(log.oldStatus, requestDetails)} → {translateStatus(log.newStatus, requestDetails)}
                          </div>
                        )}
                      </div>
                      <div className="text-left">
                        <div className="text-xs text-gray-500">
                          {new Intl.DateTimeFormat('fa-IR', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }).format(new Date(log.timestamp))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <RejectModal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={handleRejectRequest}
      />

      <ApproveModal
        isOpen={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        currentRequest={requestDetails}
        onConfirm={handleApproveRequest}
      />

      {/* Image Modal */}
      {imageModalOpen && modalImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-500"
          onClick={() => setImageModalOpen(false)}
        >
          <div
            className="bg-white p-4 rounded shadow-lg"
            onClick={e => e.stopPropagation()}
          >
            <img src={modalImage} alt="نمایش تصویر" className="max-h-[80vh] max-w-[90vw]" />
            <button
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
              onClick={() => setImageModalOpen(false)}
            >
              بستن
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Pagination component
const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}> = ({ currentPage, totalPages, onPageChange }) => {
  const maxPageItems = 5;
  const pages = [];

  let startPage = Math.max(1, currentPage - Math.floor(maxPageItems / 2));
  let endPage = Math.min(totalPages, startPage + maxPageItems - 1);

  if (endPage - startPage + 1 < maxPageItems) {
    startPage = Math.max(1, endPage - maxPageItems + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="flex justify-center mt-6">
      <nav className="flex items-center">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`mx-1 px-3 py-1 rounded ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
        >
          قبلی
        </button>

        {startPage > 1 && (
          <>
            <button onClick={() => onPageChange(1)} className="mx-1 px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700">
              1
            </button>
            {startPage > 2 && <span className="mx-1">...</span>}
          </>
        )}

        {pages.map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`mx-1 px-3 py-1 rounded ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="mx-1">...</span>}
            <button onClick={() => onPageChange(totalPages)} className="mx-1 px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700">
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`mx-1 px-3 py-1 rounded ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
        >
          بعدی
        </button>
      </nav>
    </div>
  );
};

// Modal for rejection reason
// RejectModal component is defined at the bottom of the file

// Tab components
// Updating UnderReviewTab to refresh after status changes
const UnderReviewTab: React.FC<{ onViewDetails: (requestId: string) => void; refreshTrigger?: number; onRefreshCounts?: () => void; searchQuery?: string; onResultsChange?: (hasResults: boolean) => void; onAddressClick: (addressInfo: any) => void }> = ({ onViewDetails, refreshTrigger, onRefreshCounts, searchQuery, onResultsChange, onAddressClick }) => {
  const [requests, setRequests] = useState<TestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    total: 0,
    totalPages: 1
  });
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState('');
  const itemsPerPage = 10;

  // Function to format date to Persian format - a simple version
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR').format(date);
  };

  // Fetch requests with 'reviewing' status
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const response = await getRequests({
          status: 'reviewing',
          page: pagination.currentPage,
          limit: itemsPerPage,
          search: searchQuery
        });

        if (response.success) {
          setRequests(response.data);
          setPagination({
            currentPage: response.pagination.currentPage,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          });
          setError('');
          // Notify parent about results
          if (onResultsChange) {
            onResultsChange(response.data.length > 0);
          }
        } else {
          setError('خطا در دریافت درخواست‌ها');
          if (onResultsChange) {
            onResultsChange(false);
          }
        }
      } catch (err) {
        setError('خطا در ارتباط با سرور');

        if (onResultsChange) {
          onResultsChange(false);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [pagination.currentPage, refreshTrigger]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  // Approve request with instructions
  const approveRequest = async (requestId: string, instructions?: string, samplingLocation?: string, needsContainer?: boolean, freeTransportation?: boolean, amount?: number) => {
    try {
      const response = await updateRequestStatus(requestId, 'approved', undefined, instructions, samplingLocation, needsContainer, freeTransportation, amount);

      if (response.success) {
        // Update local state
        setRequests(prevRequests =>
          prevRequests.filter(req => req._id !== requestId)
        );
        setPagination(prev => ({
          ...prev,
          total: prev.total - 1
        }));
        // Refresh tab counts
        onRefreshCounts?.();
      } else {
        setError('خطا در تایید درخواست');
      }
    } catch (err) {
      setError('خطا در ارتباط با سرور');

    }
  };

  // We don't need this anymore as rejection happens in the inline details view
  // Keeping the state for the modal though

  // We don't need this anymore as rejection happens in the inline details view

  return (
    <div className="mt-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="overflow-x-auto border border-gray-200 rounded custom-scrollbar-ongoing" style={{ maxHeight: 'calc(100vh - 300px)' }}>
        <style>{`
          .custom-scrollbar-ongoing::-webkit-scrollbar {
            height: 10px;
            width: 10px;
          }
          .custom-scrollbar-ongoing::-webkit-scrollbar-track {
            background: #F3F4F6;
            border-radius: 4px;
          }
          .custom-scrollbar-ongoing::-webkit-scrollbar-thumb {
            background: #9CA3AF;
            border-radius: 4px;
          }
          .custom-scrollbar-ongoing::-webkit-scrollbar-thumb:hover {
            background: #6B7280;
          }
          .custom-scrollbar-ongoing {
            scrollbar-width: thin;
            scrollbar-color: #9CA3AF #F3F4F6;
          }
          /* Sticky header and first column */
          .sticky-header {
            position: sticky;
            top: 0;
            z-index: 10;
            background: #F9FAFB;
          }
          .sticky-col {
            position: sticky;
            right: 0;
            z-index: 5;
            background: white;
          }
          .sticky-header.sticky-col {
            z-index: 15;
            background: #F9FAFB;
          }
          tbody tr:hover .sticky-col {
            background: #F9FAFB;
          }
        `}</style>
        {loading ? (
          <div className="py-10 text-center text-gray-600">در حال بارگذاری...</div>
        ) : requests.length === 0 ? (
          <div className="py-10 text-center text-gray-600">هیچ درخواستی در حال بررسی وجود ندارد</div>
        ) : (
          <>
            <table className="min-w-full bg-white divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="sticky-header sticky-col py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">شناسه درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">نام و نام خانوادگی</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ثبت درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ثبت درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ پاسخ آزمایشگاه</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت پاسخ آزمایشگاه</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ویرایش درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ویرایش درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">محل نمونه گیری</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ نمونه گیری</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت نمونه گیری</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">قیمت پایه</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ایاب و ذهاب</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">جزئیات</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(request => (
                  <tr key={request._id} className="border-b hover:bg-gray-50 group">
                    <td className="sticky-col py-3 px-4 text-sm shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">{request.displayId || request._id.slice(-8)}</td>
                    <td className="py-3 px-4 text-sm">{request.patientInfo?.firstName} {request.patientInfo?.lastName}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR').format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.samplingLocation?.type === 'home' ? (
                        <div className="text-xs">
                          <div>در منزل</div>
                          {request.addressInfo && (
                            <div className="mt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAddressClick(request.addressInfo);
                                }}
                                className="text-right p-2 bg-blue-50 hover:bg-blue-100 rounded text-xs text-blue-700 transition-colors w-full"
                              >
                                <div className="font-medium mb-1">آدرس: (کلیک برای مشاهده کامل)</div>
                                <div className="text-gray-600 truncate">
                                  {request.addressInfo.region && `منطقه: ${request.addressInfo.region}`}
                                </div>
                              </button>
                            </div>
                          )}
                        </div>
                      ) : 'در آزمایشگاه'}
                    </td>
                    <td className="py-3 px-4 text-sm">{request.samplingDate ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.samplingDate)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.samplingTime || '-'}</td>
                    <td className="py-3 px-4 text-sm">{(request.status === 'approved' || request.status === 'paid') ? (request.amount ? `${new Intl.NumberFormat('fa-IR').format(request.amount)} تومان` : 'رایگان') : '-'}</td>
                    <td className="py-3 px-4 text-sm">{(request.status === 'approved' || request.status === 'paid') ? (request.samplingLocation?.freeTransportation ? 'رایگان' : 'دارد') : '-'}</td>
                    <td className="py-3 px-4 text-sm"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusInfo(request.status, request).color}`}>{translateStatus(request.status, request)}</span></td>
                    <td className="py-3 px-4 text-sm"><button onClick={() => onViewDetails(request._id)} className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">جزئیات</button></td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination.totalPages > 1 && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>

      <RejectModal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={(reason) => {
          if (!selectedRequestId) return;
          updateRequestStatus(selectedRequestId, 'rejected', reason)
            .then(response => {
              if (response.success) {
                setRequests(prev => prev.filter(req => req._id !== selectedRequestId));
                setPagination(prev => ({
                  ...prev,
                  total: prev.total - 1
                }));
                // Refresh tab counts
                onRefreshCounts?.();
              } else {
                setError('خطا در رد درخواست');
              }
            })
            .catch(err => {
              setError('خطا در ارتباط با سرور');

            })
            .finally(() => {
              setRejectModalOpen(false);
              setSelectedRequestId('');
            });
        }}
      />

      <ApproveModal
        isOpen={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        currentRequest={requests.find(req => req._id === selectedRequestId) || null}
        onConfirm={(instructions, samplingLocation, needsContainer, freeTransportation, amount) => {
          if (!selectedRequestId) return;
          approveRequest(selectedRequestId, instructions, samplingLocation, needsContainer, freeTransportation, amount)
            .then(() => {
              setApproveModalOpen(false);
              setSelectedRequestId('');
            })
            .catch(err => {

            });
        }}
      />
    </div>
  );
};

const EditedTab: React.FC<{ onViewDetails: (requestId: string) => void; refreshTrigger?: number; onRefreshCounts?: () => void; searchQuery?: string; onResultsChange?: (hasResults: boolean) => void; onAddressClick: (addressInfo: any) => void }> = ({ onViewDetails, refreshTrigger, onRefreshCounts, searchQuery, onResultsChange, onAddressClick }) => {
  const [requests, setRequests] = useState<TestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    total: 0,
    totalPages: 1
  });
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState('');
  const itemsPerPage = 10;

  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR').format(date);
  };

  // Fetch requests with 'edited' status
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const response = await getRequests({
          status: 'edited',
          page: pagination.currentPage,
          limit: itemsPerPage,
          search: searchQuery
        });

        if (response.success) {
          setRequests(response.data);
          setPagination({
            currentPage: response.pagination.currentPage,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          });
          setError('');
          if (onResultsChange) {
            onResultsChange(response.data.length > 0);
          }
        } else {
          setError('خطا در دریافت درخواست‌ها');
          if (onResultsChange) {
            onResultsChange(false);
          }
        }
      } catch (err) {
        setError('خطا در ارتباط با سرور');

        if (onResultsChange) {
          onResultsChange(false);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [pagination.currentPage, refreshTrigger]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  // Approve edited request with instructions
  const approveRequest = async (requestId: string, instructions?: string, samplingLocation?: string, needsContainer?: boolean, freeTransportation?: boolean, amount?: number) => {
    try {
      const response = await updateRequestStatus(requestId, 'approved', undefined, instructions, samplingLocation, needsContainer, freeTransportation, amount);

      if (response.success) {
        // Update local state - remove from edited list
        setRequests(prevRequests =>
          prevRequests.filter(req => req._id !== requestId)
        );
        setPagination(prev => ({
          ...prev,
          total: prev.total - 1
        }));
        setError('');
        // Refresh tab counts
        onRefreshCounts?.();
      } else {
        setError('خطا در تایید درخواست');
      }
    } catch (err) {
      setError('خطا در ارتباط با سرور');

    }
  };

  // We don't need this anymore as rejection happens in the inline details view
  // Keeping the state for the modal though

  // We don't need this anymore as rejection happens in the inline details view

  return (
    <div className="mt-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="overflow-x-auto border border-gray-200 rounded custom-scrollbar-ongoing" style={{ maxHeight: 'calc(100vh - 300px)' }}>
        <style>{`
          .custom-scrollbar-ongoing::-webkit-scrollbar {
            height: 10px;
            width: 10px;
          }
          .custom-scrollbar-ongoing::-webkit-scrollbar-track {
            background: #F3F4F6;
            border-radius: 4px;
          }
          .custom-scrollbar-ongoing::-webkit-scrollbar-thumb {
            background: #9CA3AF;
            border-radius: 4px;
          }
          .custom-scrollbar-ongoing::-webkit-scrollbar-thumb:hover {
            background: #6B7280;
          }
          .custom-scrollbar-ongoing {
            scrollbar-width: thin;
            scrollbar-color: #9CA3AF #F3F4F6;
          }
          /* Sticky header and first column */
          .sticky-header {
            position: sticky;
            top: 0;
            z-index: 10;
            background: #F9FAFB;
          }
          .sticky-col {
            position: sticky;
            right: 0;
            z-index: 5;
            background: white;
          }
          .sticky-header.sticky-col {
            z-index: 15;
            background: #F9FAFB;
          }
          tbody tr:hover .sticky-col {
            background: #F9FAFB;
          }
        `}</style>
        {loading ? (
          <div className="py-10 text-center text-gray-600">در حال بارگذاری...</div>
        ) : requests.length === 0 ? (
          <div className="py-10 text-center text-gray-600">
            <p>هیچ درخواستی ویرایش شده وجود ندارد</p>
            <p className="text-sm text-gray-500 mt-2">این بخش شامل درخواست‌های معمولی ویرایش شده و درخواست‌های رد شده که کاربران آن‌ها را دوباره ویرایش کرده‌اند، می‌باشد</p>
          </div>
        ) : (
          <>
            <table className="min-w-full bg-white divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="sticky-header sticky-col py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">شناسه درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">نام و نام خانوادگی</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ثبت درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ثبت درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ پاسخ آزمایشگاه</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت پاسخ آزمایشگاه</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ویرایش درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ویرایش درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">محل نمونه گیری</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ نمونه گیری</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت نمونه گیری</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">قیمت پایه</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ایاب و ذهاب</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ویرایش</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">جزئیات</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(request => (
                  <tr key={request._id} className="border-b hover:bg-gray-50 group">
                    <td className="sticky-col py-3 px-4 text-sm shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">{request.displayId || request._id.slice(-8)}</td>
                    <td className="py-3 px-4 text-sm">{request.patientInfo?.firstName} {request.patientInfo?.lastName}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR').format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.samplingLocation?.type === 'home' ? (
                        <div className="text-xs">
                          <div>در منزل</div>
                          {request.addressInfo && (
                            <div className="mt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAddressClick(request.addressInfo);
                                }}
                                className="text-right p-2 bg-blue-50 hover:bg-blue-100 rounded text-xs text-blue-700 transition-colors w-full"
                              >
                                <div className="font-medium mb-1">آدرس: (کلیک برای مشاهده کامل)</div>
                                <div className="text-gray-600 truncate">
                                  {request.addressInfo.region && `منطقه: ${request.addressInfo.region}`}
                                </div>
                              </button>
                            </div>
                          )}
                        </div>
                      ) : 'در آزمایشگاه'}
                    </td>
                    <td className="py-3 px-4 text-sm">{request.samplingDate ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.samplingDate)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.samplingTime || '-'}</td>
                    <td className="py-3 px-4 text-sm">{(request.status === 'approved' || request.status === 'paid') ? (request.amount ? `${new Intl.NumberFormat('fa-IR').format(request.amount)} تومان` : 'رایگان') : '-'}</td>
                    <td className="py-3 px-4 text-sm">{(request.status === 'approved' || request.status === 'paid') ? (request.samplingLocation?.freeTransportation ? 'رایگان' : 'دارد') : '-'}</td>
                    <td className="py-3 px-4 text-sm">{formatDate(request.updatedAt)}</td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusInfo(request.status, request).color}`}>
                        {translateStatus(request.status, request)}
                        {request.status === 'edited' && request.editVersion && request.editVersion > 0 && (
                          <span className="mr-1 bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded text-xs">
                            {request.editVersion}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm"><button onClick={() => onViewDetails(request._id)} className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">جزئیات</button></td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination.totalPages > 1 && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>

      <RejectModal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={(reason) => {
          if (!selectedRequestId) return;
          updateRequestStatus(selectedRequestId, 'rejected', reason)
            .then(response => {
              if (response.success) {
                setRequests(prev => prev.filter(req => req._id !== selectedRequestId));
                setPagination(prev => ({
                  ...prev,
                  total: prev.total - 1
                }));
                // Refresh tab counts
                onRefreshCounts?.();
              } else {
                setError('خطا در رد درخواست');
              }
            })
            .catch(err => {
              setError('خطا در ارتباط با سرور');

            })
            .finally(() => {
              setRejectModalOpen(false);
              setSelectedRequestId('');
            });
        }}
      />

      <ApproveModal
        isOpen={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        currentRequest={requests.find(req => req._id === selectedRequestId) || null}
        onConfirm={(instructions, samplingLocation, needsContainer, freeTransportation, amount) => {
          if (!selectedRequestId) return;
          approveRequest(selectedRequestId, instructions, samplingLocation, needsContainer, freeTransportation, amount)
            .then(() => {
              setApproveModalOpen(false);
              setSelectedRequestId('');
            })
            .catch(err => {

            });
        }}
      />
    </div>
  );
};

const ApprovedTab: React.FC<{ onViewDetails: (requestId: string) => void; searchQuery?: string; onResultsChange?: (hasResults: boolean) => void; onAddressClick: (addressInfo: any) => void }> = ({ onViewDetails, searchQuery, onResultsChange, onAddressClick }) => {
  const [requests, setRequests] = useState<TestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    total: 0,
    totalPages: 1
  });
  const itemsPerPage = 10;

  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR').format(date);
  };

  // Fetch requests with 'approved' status
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const response = await getRequests({
          status: 'approved',
          page: pagination.currentPage,
          limit: itemsPerPage,
          search: searchQuery
        });

        if (response.success) {
          setRequests(response.data);
          setPagination({
            currentPage: response.pagination.currentPage,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          });
          setError('');
          if (onResultsChange) {
            onResultsChange(response.data.length > 0);
          }
        } else {
          setError('خطا در دریافت درخواست‌ها');
          if (onResultsChange) {
            onResultsChange(false);
          }
        }
      } catch (err) {
        setError('خطا در ارتباط با سرور');

        if (onResultsChange) {
          onResultsChange(false);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [pagination.currentPage, searchQuery]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  return (
    <div className="mt-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        {loading ? (
          <div className="py-10 text-center text-gray-600">در حال بارگذاری...</div>
        ) : requests.length === 0 ? (
          <div className="py-10 text-center text-gray-600">هیچ درخواست تایید شده‌ای وجود ندارد</div>
        ) : (
          <>
            <table className="min-w-full bg-white divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="sticky-header sticky-col py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">شناسه درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">نام و نام خانوادگی</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ثبت درخواست</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ثبت درخواست</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ پاسخ آزمایشگاه</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت پاسخ آزمایشگاه</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ویرایش درخواست</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ویرایش درخواست</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">محل نمونه گیری</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ نمونه گیری</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت نمونه گیری</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">قیمت پایه</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ایاب و ذهاب</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ تایید</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت تمدید</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">دستورالعمل‌ها</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">جزئیات</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(request => (
                  <tr key={request._id} className="border-b hover:bg-gray-50 group">
                    <td className="sticky-col py-3 px-4 text-sm shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">{request.displayId || request._id.slice(-8)}</td>
                    <td className="py-3 px-4 text-sm">{request.patientInfo?.firstName} {request.patientInfo?.lastName}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR').format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.samplingLocation?.type === 'home' ? (
                        <div className="text-xs">
                          <div>در منزل</div>
                          {request.addressInfo && (
                            <div className="mt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAddressClick(request.addressInfo);
                                }}
                                className="text-right p-2 bg-blue-50 hover:bg-blue-100 rounded text-xs text-blue-700 transition-colors w-full"
                              >
                                <div className="font-medium mb-1">آدرس: (کلیک برای مشاهده کامل)</div>
                                <div className="text-gray-600 truncate">
                                  {request.addressInfo.region && `منطقه: ${request.addressInfo.region}`}
                                </div>
                              </button>
                            </div>
                          )}
                        </div>
                      ) : 'در آزمایشگاه'}
                    </td>
                    <td className="py-3 px-4 text-sm">{request.samplingDate ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.samplingDate)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.samplingTime || '-'}</td>
                    <td className="py-3 px-4 text-sm">{(request.status === 'approved' || request.status === 'paid') ? (request.amount ? `${new Intl.NumberFormat('fa-IR').format(request.amount)} تومان` : 'رایگان') : '-'}</td>
                    <td className="py-3 px-4 text-sm">{(request.status === 'approved' || request.status === 'paid') ? (request.samplingLocation?.freeTransportation ? 'رایگان' : 'دارد') : '-'}</td>
                    <td className="py-3 px-4 text-sm">{formatDate(request.updatedAt)}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.paymentExtensionStatus ? (
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${
                          request.paymentExtensionStatus === 'approved' 
                            ? 'bg-green-100 text-green-700' 
                            : request.paymentExtensionStatus === 'rejected'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {request.paymentExtensionStatus === 'approved' 
                            ? '✅ تایید شده' 
                            : request.paymentExtensionStatus === 'rejected'
                            ? '❌ رد شده'
                            : '⏳ در انتظار'
                          }
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm max-w-xs">
                      {request.instructions ? (
                        <div className="text-green-700 text-xs truncate" title={request.instructions}>
                          <span className="inline-flex items-center">
                            <span className="ml-1">📋</span>
                            {request.instructions}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-500">---</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusInfo(request.status, request).color}`}>{translateStatus(request.status, request)}</span></td>
                    <td className="py-3 px-4 text-sm"><button onClick={() => onViewDetails(request._id)} className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">جزئیات</button></td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination.totalPages > 1 && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const RejectedTab: React.FC<{ onViewDetails: (requestId: string) => void; searchQuery?: string; onResultsChange?: (hasResults: boolean) => void; onAddressClick: (addressInfo: any) => void }> = ({ onViewDetails, searchQuery, onResultsChange, onAddressClick }) => {
  const [requests, setRequests] = useState<TestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resettingDeadline, setResettingDeadline] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    total: 0,
    totalPages: 1
  });
  const itemsPerPage = 10;

  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR').format(date);
  };

  // Fetch requests with 'rejected' status
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const response = await getRequests({
          status: 'rejected',
          page: pagination.currentPage,
          limit: itemsPerPage,
          search: searchQuery
        });

        if (response.success) {
          setRequests(response.data);
          setPagination({
            currentPage: response.pagination.currentPage,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          });
          setError('');
          if (onResultsChange) {
            onResultsChange(response.data.length > 0);
          }
        } else {
          setError('خطا در دریافت درخواست‌ها');
          if (onResultsChange) {
            onResultsChange(false);
          }
        }
      } catch (err) {
        setError('خطا در ارتباط با سرور');

        if (onResultsChange) {
          onResultsChange(false);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [pagination.currentPage, searchQuery]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  // Handle reset edit deadline
  const handleResetEditDeadline = async (requestId: string) => {
    try {
      setResettingDeadline(requestId);
      const response = await resetEditDeadline(requestId);
      
      if (response.success) {
        // Update the local state to reflect the new editDeadline
        setRequests(prevRequests => 
          prevRequests.map(req => 
            req._id === requestId 
              ? { ...req, editDeadline: response.data.editDeadline }
              : req
          )
        );
        
        toast.success(response.message || 'مهلت ویرایش با موفقیت بازنشانی شد');
      } else {
        toast.error(response.message || 'خطا در بازنشانی مهلت ویرایش');
      }
    } catch (error) {

      toast.error('خطا در بازنشانی مهلت ویرایش');
    } finally {
      setResettingDeadline(null);
    }
  };

  return (
    <div className="mt-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="overflow-x-auto border border-gray-200 rounded custom-scrollbar-ongoing" style={{ maxHeight: 'calc(100vh - 300px)' }}>
        <style>{`
          .custom-scrollbar-ongoing::-webkit-scrollbar {
            height: 10px;
            width: 10px;
          }
          .custom-scrollbar-ongoing::-webkit-scrollbar-track {
            background: #F3F4F6;
            border-radius: 4px;
          }
          .custom-scrollbar-ongoing::-webkit-scrollbar-thumb {
            background: #9CA3AF;
            border-radius: 4px;
          }
          .custom-scrollbar-ongoing::-webkit-scrollbar-thumb:hover {
            background: #6B7280;
          }
          .custom-scrollbar-ongoing {
            scrollbar-width: thin;
            scrollbar-color: #9CA3AF #F3F4F6;
          }
          /* Sticky header and first column */
          .sticky-header {
            position: sticky;
            top: 0;
            z-index: 10;
            background: #F9FAFB;
          }
          .sticky-col {
            position: sticky;
            right: 0;
            z-index: 5;
            background: white;
          }
          .sticky-header.sticky-col {
            z-index: 15;
            background: #F9FAFB;
          }
          tbody tr:hover .sticky-col {
            background: #F9FAFB;
          }
        `}</style>
        {loading ? (
          <div className="py-10 text-center text-gray-600">در حال بارگذاری...</div>
        ) : requests.length === 0 ? (
          <div className="py-10 text-center text-gray-600">هیچ درخواست رد شده‌ای وجود ندارد</div>
        ) : (
          <>
            <table className="min-w-full bg-white divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="sticky-header sticky-col py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">شناسه درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">نام و نام خانوادگی</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ثبت درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ثبت درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ پاسخ آزمایشگاه</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت پاسخ آزمایشگاه</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ویرایش درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ویرایش درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">محل نمونه گیری</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ نمونه گیری</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت نمونه گیری</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ رد</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">مهلت ویرایش</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">جزئیات</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(request => (
                  <tr key={request._id} className="border-b hover:bg-gray-50 group">
                    <td className="sticky-col py-3 px-4 text-sm shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">{request.displayId || request._id.slice(-8)}</td>
                    <td className="py-3 px-4 text-sm">{request.patientInfo?.firstName} {request.patientInfo?.lastName}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR').format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.samplingLocation?.type === 'home' ? (
                        <div className="text-xs">
                          <div>در منزل</div>
                          {request.addressInfo && (
                            <div className="mt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAddressClick(request.addressInfo);
                                }}
                                className="text-right p-2 bg-blue-50 hover:bg-blue-100 rounded text-xs text-blue-700 transition-colors w-full"
                              >
                                <div className="font-medium mb-1">آدرس: (کلیک برای مشاهده کامل)</div>
                                <div className="text-gray-600 truncate">
                                  {request.addressInfo.region && `منطقه: ${request.addressInfo.region}`}
                                </div>
                              </button>
                            </div>
                          )}
                        </div>
                      ) : 'در آزمایشگاه'}
                    </td>
                    <td className="py-3 px-4 text-sm">{request.samplingDate ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.samplingDate)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.samplingTime || '-'}</td>
                    <td className="py-3 px-4 text-sm">{formatDate(request.updatedAt)}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.editDeadline ? (
                        <div className="text-xs">
                          <div className={`${isEditDeadlinePassed(request.editDeadline) ? 'text-red-600' : 'text-green-600'}`}>
                            {formatRemainingEditTime(request.editDeadline)}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-500">---</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusInfo(request.status, request).color}`}>{translateStatus(request.status, request)}</span></td>
                    <td className="py-3 px-4 text-sm"><button onClick={() => onViewDetails(request._id)} className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">جزئیات</button></td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination.totalPages > 1 && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const OngoingRequests: React.FC = () => {
  const [activeTab, setActiveTab] = useState('reviewing'); // Default tab
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  
  // State for tab counts
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({
    reviewing: 0,
    incomplete: 0,
    canceled: 0,
    edited: 0,
    approved: 0,
    rejected: 0,
    paid: 0,
    expired: 0
  });

  // State for tab visibility (when search is active)
  const [tabHasResults, setTabHasResults] = useState<Record<string, boolean>>({
    reviewing: true,
    edited: true,
    approved: true,
    rejected: true
  });

  // State for payment extension count
  const [paymentExtensionCount, setPaymentExtensionCount] = useState<number>(0);
  
  // State for address modal
  const [addressModalOpen, setAddressModalOpen] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<TestRequest['addressInfo'] | null>(null);

  // Function to fetch tab counts
  const fetchTabCounts = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(API_CONFIG.getUrl('/admin/requests/counts'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTabCounts(data.data || {});
      }
    } catch (error) {

    }
  };

  // Function to fetch payment extension count
  const fetchPaymentExtensionCount = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(API_CONFIG.getUrl('/admin/requests/payment-extension-count'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentExtensionCount(data.data?.paymentExtensionCount || 0);
      }
    } catch (error) {

    }
  };

  // Load tab counts and payment extension count on component mount
  useEffect(() => {
    fetchTabCounts();
    fetchPaymentExtensionCount();
  }, []);

  // Setup socket listener for new test requests
  useEffect(() => {
    const handleNewTestRequest = (notification: any) => {

      // Trigger refresh of the current tab
      setRefreshTrigger(prev => prev + 1);
      
      // Refresh tab counts and payment extension count
      fetchTabCounts();
      fetchPaymentExtensionCount();
      
      // Show toast notification
      toast.success(`درخواست جدید از ${notification.testRequest.userInfo} دریافت شد`, {
        position: "top-right",
        autoClose: 5000,
      });
    };

    // Set up socket listener
    socketService.setOnNewTestRequest(handleNewTestRequest);

    // Cleanup
    return () => {
      socketService.setOnNewTestRequest(() => {});
    };
  }, []);

  // Tab switching handler
  const handleTabChange = (tabId: string) => {
    // Close any open details if switching tabs
    if (selectedRequestId) {
      setSelectedRequestId(null);
    }
    setActiveTab(tabId);
  };

  // Handle search
  const handleSearch = async () => {
    setActiveSearchQuery(searchQuery);
    setRefreshTrigger(prev => prev + 1);
    
    // Check which tabs have results
    if (searchQuery.trim()) {
      await checkTabsForResults(searchQuery);
    }
  };

  // Check which tabs have results
  const checkTabsForResults = async (search: string) => {
    try {
      const statuses = ['reviewing', 'edited', 'approved', 'rejected'];
      const results: Record<string, boolean> = {};
      
      for (const status of statuses) {
        try {
          const response = await getRequests({
            status,
            page: 1,
            limit: 1,
            search
          });
          results[status] = response.data.length > 0;
        } catch (error) {

          results[status] = true; // Show tab if there's an error
        }
      }
      
      setTabHasResults(results);
      
      // If current tab has no results, switch to first tab with results
      if (!results[activeTab]) {
        const firstTabWithResults = statuses.find(tab => results[tab]);
        if (firstTabWithResults) {
          setActiveTab(firstTabWithResults);
        }
      }
    } catch (error) {

    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearchQuery('');
    setRefreshTrigger(prev => prev + 1);
    // Reset all tabs to visible
    setTabHasResults({
      reviewing: true,
      edited: true,
      approved: true,
      rejected: true
    });
  };

  // Handle search on Enter key
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Details handlers
  const handleViewDetails = (requestId: string) => {
    setSelectedRequestId(requestId);
    // Scroll to top when details modal opens
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCloseDetails = () => {
    setSelectedRequestId(null);
  };

  // Handle status change (approve/reject)
  const handleStatusChange = async (requestId: string, status: string, reason?: string, instructions?: string, samplingLocation?: string, needsContainer?: boolean, freeTransportation?: boolean, amount?: number): Promise<void> => {
    try {
      const response = await updateRequestStatus(requestId, status, reason, instructions, samplingLocation, needsContainer, freeTransportation, amount);

      if (response.success) {
        // Close details view after successful status change
        setSelectedRequestId(null);
        // Trigger refresh of the current tab
        setRefreshTrigger(prev => prev + 1);
        // Refresh tab counts and payment extension count
        fetchTabCounts();
        fetchPaymentExtensionCount();
      }
    } catch (error) {

    }
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">درخواست‌های جاری</h1>

      {/* Inline Request Details (shown when a request is selected) - Full Screen Mode */}
      {selectedRequestId ? (
        <div className="bg-white border border-gray-200 rounded-lg shadow p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">جزئیات درخواست</h2>
            <button
              onClick={handleCloseDetails}
              className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md font-medium transition-colors"
            >
              ← برگشت به لیست
            </button>
          </div>
          <RequestDetails
            requestId={selectedRequestId}
            onClose={handleCloseDetails}
            onStatusChange={handleStatusChange}
            showActionButtons={activeTab === 'reviewing' || activeTab === 'edited'}
          />
        </div>
      ) : (
        <>
          {/* Search Field */}
          <div className="mb-6">
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="جستجو بر اساس نام، نام خانوادگی، کد ملی، موبایل یا شناسه..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={handleSearch}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                جستجو
              </button>
              {activeSearchQuery && (
                <button
                  onClick={handleClearSearch}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  پاک کردن
                </button>
              )}
            </div>
          </div>

          {/* Payment Extension Request Message */}
          {paymentExtensionCount > 0 && (
            <div className="mb-6 p-4 rounded-md bg-yellow-50 border border-yellow-200">
              <div className="flex items-center">
                <span className="text-yellow-600 text-xl ml-2">⏰</span>
                <div>
                  <h3 className="text-lg font-semibold text-yellow-700">
                    {paymentExtensionCount} درخواست وجود دارد که درخواست تمدید پرداخت داده‌اند
                  </h3>
                  <p className="text-yellow-600 text-sm mt-1">
                    می‌توانید در تب درخواست‌های منقضی شده در بخش سایر درخواست‌ها مشاهده کنید
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tab buttons */}
          <div className="flex flex-wrap border-b border-gray-200">
            {(!activeSearchQuery || tabHasResults.reviewing) && (
              <button
                className={`px-4 py-2 font-medium text-sm flex items-center ${activeTab === 'reviewing'
                  ? 'border-b-2 border-blue-500 text-blue-500'
                  : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => handleTabChange('reviewing')}
              >
                در حال بررسی
                <TabBadge count={tabCounts.reviewing} />
              </button>
            )}

            {(!activeSearchQuery || tabHasResults.edited) && (
              <button
                className={`px-4 py-2 font-medium text-sm flex items-center ${activeTab === 'edited' 
                  ? 'border-b-2 border-blue-500 text-blue-500' 
                  : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => handleTabChange('edited')}
                title="درخواست‌های ویرایش شده شامل: درخواست‌های معمولی ویرایش شده و درخواست‌های رد شده که کاربران آن‌ها را ویرایش کرده‌اند"
              >
                ویرایش شده
                <TabBadge count={tabCounts.edited} />
              </button>
            )}

            {(!activeSearchQuery || tabHasResults.approved) && (
              <button
                className={`px-4 py-2 font-medium text-sm flex items-center ${activeTab === 'approved' 
                  ? 'border-b-2 border-blue-500 text-blue-500' 
                  : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => handleTabChange('approved')}
              >
                تایید شده
                <TabBadge count={tabCounts.approved} />
              </button>
            )}

            {(!activeSearchQuery || tabHasResults.rejected) && (
              <button
                className={`px-4 py-2 font-medium text-sm flex items-center ${activeTab === 'rejected' 
                  ? 'border-b-2 border-blue-500 text-blue-500' 
                  : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => handleTabChange('rejected')}
              >
                تایید نشده
                <TabBadge count={tabCounts.rejected} />
              </button>
            )}
          </div>
          
          {/* Tab content */}
          <div className="mt-4">
            {activeTab === 'reviewing' && <UnderReviewTab onViewDetails={handleViewDetails} refreshTrigger={refreshTrigger} onRefreshCounts={fetchTabCounts} searchQuery={activeSearchQuery} onResultsChange={(hasResults) => setTabHasResults(prev => ({ ...prev, reviewing: hasResults }))} onAddressClick={(addressInfo) => { setSelectedAddress(addressInfo); setAddressModalOpen(true); }} />}
            {activeTab === 'edited' && <EditedTab onViewDetails={handleViewDetails} refreshTrigger={refreshTrigger} onRefreshCounts={fetchTabCounts} searchQuery={activeSearchQuery} onResultsChange={(hasResults) => setTabHasResults(prev => ({ ...prev, edited: hasResults }))} onAddressClick={(addressInfo) => { setSelectedAddress(addressInfo); setAddressModalOpen(true); }} />}
            {activeTab === 'approved' && <ApprovedTab onViewDetails={handleViewDetails} searchQuery={activeSearchQuery} onResultsChange={(hasResults) => setTabHasResults(prev => ({ ...prev, approved: hasResults }))} onAddressClick={(addressInfo) => { setSelectedAddress(addressInfo); setAddressModalOpen(true); }} />}
            {activeTab === 'rejected' && <RejectedTab onViewDetails={handleViewDetails} searchQuery={activeSearchQuery} onResultsChange={(hasResults) => setTabHasResults(prev => ({ ...prev, rejected: hasResults }))} onAddressClick={(addressInfo) => { setSelectedAddress(addressInfo); setAddressModalOpen(true); }} />}
          </div>
        </>
      )}

      {/* Address Modal */}
      {addressModalOpen && selectedAddress && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setAddressModalOpen(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">آدرس کامل</h3>
              <button
                onClick={() => setAddressModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="space-y-3 text-right">
              {selectedAddress.region && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">منطقه:</span>
                  <span className="text-gray-600">{selectedAddress.region}</span>
                </div>
              )}
              {selectedAddress.field && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">میدان:</span>
                  <span className="text-gray-600">{selectedAddress.field}</span>
                </div>
              )}
              {selectedAddress.street && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">خیابان:</span>
                  <span className="text-gray-600">{selectedAddress.street}</span>
                </div>
              )}
              {selectedAddress.alley && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">کوچه:</span>
                  <span className="text-gray-600">{selectedAddress.alley}</span>
                </div>
              )}
              {selectedAddress.building && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">ساختمان:</span>
                  <span className="text-gray-600">{selectedAddress.building}</span>
                </div>
              )}
              {selectedAddress.unit && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">واحد:</span>
                  <span className="text-gray-600">{selectedAddress.unit}</span>
                </div>
              )}
              {selectedAddress.number && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">پلاک:</span>
                  <span className="text-gray-600">{selectedAddress.number}</span>
                </div>
              )}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="font-medium text-gray-700 mb-2">آدرس کامل:</div>
                <div className="text-gray-600 leading-relaxed">
                  {[
                    selectedAddress.region && `منطقه ${selectedAddress.region}`,
                    selectedAddress.field && `میدان ${selectedAddress.field}`,
                    selectedAddress.street && `خیابان ${selectedAddress.street}`,
                    selectedAddress.alley && `کوچه ${selectedAddress.alley}`,
                    selectedAddress.building && `ساختمان ${selectedAddress.building}`,
                    selectedAddress.unit && `واحد ${selectedAddress.unit}`,
                    selectedAddress.number && `پلاک ${selectedAddress.number}`
                  ].filter(Boolean).join('، ')}
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setAddressModalOpen(false)}
                className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 transition-colors"
              >
                بستن
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ...
interface RejectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

const RejectModal: React.FC<RejectModalProps> = ({ isOpen, onClose, onConfirm }) => {
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [reasons, setReasons] = useState<Command[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState<string>('');
  
  useEffect(() => {
    if (isOpen) {
      loadReasons();
    } else {
      setSelectedReasons([]);
      setError('');
      setAmount('');
    }
  }, [isOpen]);

  const loadReasons = async () => {
    try {
      setLoading(true);
      const commands = await commandService.getCommands();
      setReasons(commands.reasons);
    } catch (error) {

      setError('خطا در بارگیری دلایل');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (selectedReasons.length === 0) {
      setError('لطفا حداقل یک دلیل رد درخواست را انتخاب کنید');
      return;
    }
    
    // Convert selected reason IDs to text
    const selectedTexts = selectedReasons
      .map(id => reasons.find(reason => reason._id === id)?.text)
      .filter(Boolean);
    
    const reasonsText = selectedTexts.join('\n• ');
    const finalReasons = selectedTexts.length > 0 ? `• ${reasonsText}` : '';
    
    onConfirm(finalReasons);
    setSelectedReasons([]);
    setError('');
    setAmount('');
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="fixed inset-0 bg-black opacity-50"></div>
      <div className="bg-white rounded-lg p-6 max-w-lg w-full z-10 max-h-[80vh] min-h-[50vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">دلایل رد درخواست</h3>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        
        <p className="text-gray-600 mb-4 text-sm">
          دلایل رد درخواست را انتخاب کنید:
        </p>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4 text-sm">
            {error}
          </div>
        )}
        
        {loading ? (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-red-500"></div>
            <p className="mt-2 text-sm text-gray-600">در حال بارگیری دلایل...</p>
          </div>
        ) : (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              انتخاب دلایل رد
            </label>
            <Select
              isMulti
              options={reasons.map(reason => ({
                value: reason._id,
                label: reason.text
              }))}
              value={reasons
                .filter(reason => selectedReasons.includes(reason._id))
                .map(reason => ({
                  value: reason._id,
                  label: reason.text
                }))}
              onChange={(selectedOptions) => {
                const selectedIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
                setSelectedReasons(selectedIds);
              }}
              placeholder="دلایل رد را انتخاب کنید..."
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              controlShouldRenderValue={true}
              isSearchable={true}
              noOptionsMessage={() => "هیچ دلیلی یافت نشد"}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  minHeight: '42px',
                  border: state.isFocused ? '2px solid #ef4444' : '1px solid #d1d5db',
                  boxShadow: state.isFocused ? '0 0 0 1px #ef4444' : 'none',
                  '&:hover': {
                    border: '1px solid #ef4444'
                  }
                }),
                multiValue: (provided) => ({
                  ...provided,
                  backgroundColor: '#fee2e2',
                  color: '#dc2626'
                }),
                multiValueLabel: (provided) => ({
                  ...provided,
                  color: '#dc2626',
                  fontSize: '12px'
                }),
                multiValueRemove: (provided) => ({
                  ...provided,
                  color: '#dc2626',
                  ':hover': {
                    backgroundColor: '#fecaca',
                    color: '#991b1b'
                  }
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected 
                    ? '#fee2e2' 
                    : state.isFocused 
                    ? '#fef2f2' 
                    : 'white',
                  color: state.isSelected ? '#dc2626' : '#374151',
                  ':active': {
                    backgroundColor: '#fecaca'
                  }
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: '#9ca3af',
                  fontSize: '14px'
                }),
                menuList: (provided) => ({
                  ...provided,
                  maxHeight: '200px'
                })
              }}
            />
          </div>
        )}

        {selectedReasons.length > 0 && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
            <h4 className="text-sm font-medium text-red-800 mb-2">دلایل انتخاب شده:</h4>
            <div className="text-sm text-red-700">
              {selectedReasons.map((id, index) => {
                const reason = reasons.find(r => r._id === id);
                return reason ? (
                  <div key={id} className="flex items-start mb-1">
                    <span className="ml-2">•</span>
                    <span>{reason.text}</span>
                  </div>
                ) : null;
              })}
            </div>
          </div>
        )}
        
        <div className="text-xs text-gray-500 mb-4">
          نکته: دلایل انتخاب شده برای کاربر نمایش داده خواهد شد.
        </div>

        <div className="flex justify-end space-x-4 gap-2">
          <button
            onClick={onClose}
            className="mr-2 px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded text-gray-800"
          >
            انصراف
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded text-white disabled:bg-red-400"
          >
            رد کردن
          </button>
        </div>
      </div>
    </div>
  );
};

interface ApproveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (instructions: string, samplingLocation: string, needsContainer: boolean, freeTransportation: boolean, amount?: number) => void;
  currentRequest?: TestRequest | null;
}

const ApproveModal: React.FC<ApproveModalProps> = ({ isOpen, onClose, onConfirm, currentRequest }) => {
  const [selectedInstructions, setSelectedInstructions] = useState<string[]>([]);
  const [instructions, setInstructions] = useState<Command[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [samplingLocation, setSamplingLocation] = useState<'home' | 'lab'>('home');
  const [needsContainer, setNeedsContainer] = useState(false);
  const [freeTransportation, setFreeTransportation] = useState(false);
  const [amount, setAmount] = useState<string>('');
  
  useEffect(() => {
    if (isOpen) {
      loadInstructions();
      
      // Pre-select sampling location based on current request
      if (currentRequest && currentRequest.samplingLocation) {
        setSamplingLocation(currentRequest.samplingLocation.type);
        setNeedsContainer(currentRequest.samplingLocation.needsContainer || false);
        setFreeTransportation(currentRequest.samplingLocation.freeTransportation || false);
      } else {
        // Default to home if no request data available
        setSamplingLocation('home');
        setNeedsContainer(false);
        setFreeTransportation(false);
      }
    } else {
      setSelectedInstructions([]);
      setError('');
      setSamplingLocation('home');
      setNeedsContainer(false);
      setFreeTransportation(false);
      setAmount('');
    }
  }, [isOpen, currentRequest]);

  const loadInstructions = async () => {
    try {
      setLoading(true);
      const commands = await commandService.getCommands();
      setInstructions(commands.instructions);
    } catch (error) {

      setError('خطا در بارگیری دستورالعمل‌ها');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    // Convert selected instruction IDs to text
    const selectedTexts = selectedInstructions
      .map(id => instructions.find(inst => inst._id === id)?.text)
      .filter(Boolean);
    
    const instructionsText = selectedTexts.join('\n• ');
    const finalInstructions = selectedTexts.length > 0 ? `• ${instructionsText}` : '';
    
    onConfirm(finalInstructions, samplingLocation, needsContainer, freeTransportation, amount !== '' ? parseFloat(amount) : undefined);
    setSelectedInstructions([]);
    setError('');
    setSamplingLocation('home');
    setNeedsContainer(false);
    setFreeTransportation(false);
    setAmount('');
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="fixed inset-0 bg-black opacity-50"></div>
      <div className="bg-white rounded-lg p-6 max-w-lg w-full z-10 max-h-[80vh] min-h-[50vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">دستورالعمل‌ها برای کاربر</h3>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4 text-sm">
            {error}
          </div>
        )}
        
        {loading ? (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-green-500"></div>
            <p className="mt-2 text-sm text-gray-600">در حال بارگیری دستورالعمل‌ها...</p>
          </div>
        ) : (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              انتخاب دستورالعمل‌ها (اختیاری)
            </label>
            <Select
              isMulti
              options={instructions.map(instruction => ({
                value: instruction._id,
                label: instruction.text
              }))}
              value={instructions
                .filter(instruction => selectedInstructions.includes(instruction._id))
                .map(instruction => ({
                  value: instruction._id,
                  label: instruction.text
                }))}
              onChange={(selectedOptions) => {
                const selectedIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
                setSelectedInstructions(selectedIds);
              }}
              placeholder="دستورالعمل‌ها را انتخاب کنید..."
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              controlShouldRenderValue={true}
              isSearchable={true}
              noOptionsMessage={() => "هیچ دستورالعملی یافت نشد"}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  minHeight: '42px',
                  border: state.isFocused ? '2px solid #10b981' : '1px solid #d1d5db',
                  boxShadow: state.isFocused ? '0 0 0 1px #10b981' : 'none',
                  '&:hover': {
                    border: '1px solid #10b981'
                  }
                }),
                multiValue: (provided) => ({
                  ...provided,
                  backgroundColor: '#d1fae5',
                  color: '#065f46'
                }),
                multiValueLabel: (provided) => ({
                  ...provided,
                  color: '#065f46',
                  fontSize: '12px'
                }),
                multiValueRemove: (provided) => ({
                  ...provided,
                  color: '#065f46',
                  ':hover': {
                    backgroundColor: '#a7f3d0',
                    color: '#047857'
                  }
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected 
                    ? '#d1fae5' 
                    : state.isFocused 
                    ? '#ecfdf5' 
                    : 'white',
                  color: state.isSelected ? '#065f46' : '#374151',
                  ':active': {
                    backgroundColor: '#a7f3d0'
                  }
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: '#9ca3af',
                  fontSize: '14px'
                }),
                menuList: (provided) => ({
                  ...provided,
                  maxHeight: '200px'
                })
              }}
            />
          </div>
        )}

        {selectedInstructions.length > 0 && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
            <h4 className="text-sm font-medium text-green-800 mb-2">دستورالعمل‌های انتخاب شده:</h4>
            <div className="text-sm text-green-700">
              {selectedInstructions.map((id, index) => {
                const instruction = instructions.find(inst => inst._id === id);
                return instruction ? (
                  <div key={id} className="flex items-start mb-1">
                    <span className="ml-2">•</span>
                    <span>{instruction.text}</span>
                  </div>
                ) : null;
              })}
            </div>
          </div>
        )}
        
        <div className="text-xs text-gray-500 mb-4">
          نکته: دستورالعمل‌های انتخاب شده برای کاربر نمایش داده خواهد شد.
        </div>
        
        {/* Sampling Location Selection */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <h4 className="text-sm font-medium text-blue-800 mb-2">محل نمونه‌گیری:</h4>
          <div className="space-y-2">
            <label className="flex items-center space-x-3 space-x-reverse cursor-pointer">
              <input
                type="radio"
                value="home"
                checked={samplingLocation === 'home'}
                onChange={(e) => setSamplingLocation(e.target.value as 'home' | 'lab')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="text-sm text-gray-700 pr-2">منزل</span>
            </label>
            <label className="flex items-center space-x-3 space-x-reverse cursor-pointer">
              <input
                type="radio"
                value="lab"
                checked={samplingLocation === 'lab'}
                onChange={(e) => setSamplingLocation(e.target.value as 'home' | 'lab')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="text-sm text-gray-700 pr-2">آزمایشگاه</span>
            </label>
          </div>
        </div>
        
        {/* Home Sampling Options - Only shown when 'home' is selected */}
        { (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <h4 className="text-sm font-medium text-yellow-800 mb-2">گزینه‌های نمونه‌گیری :</h4>
            <div className="space-y-2">
              <label className="flex items-center space-x-3 space-x-reverse cursor-pointer">
                <input
                  type="checkbox"
                  checked={needsContainer}
                  onChange={(e) => setNeedsContainer(e.target.checked)}
                  className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700  pr-2">نیاز به ظرف دارد</span>
              </label>
              { (samplingLocation === 'home') && (
              <label className="flex items-center space-x-3 space-x-reverse cursor-pointer">
                <input
                  type="checkbox"
                  checked={freeTransportation}
                  onChange={(e) => setFreeTransportation(e.target.checked)}
                  className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700 pr-2">رایگان کردن ایاب و ذهاب</span>
              </label>
              )}
            </div>
          </div>
        )}
        
        {/* Amount Input Field */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            مبلغ (تومان)
          </label>
          <FormattedNumberInput
            value={amount}
            onChange={setAmount}
            placeholder="مبلغ را وارد کنید... (صفر برای رایگان)"
            min={0}
            step={1000}
            showWords={true}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
          />
        </div>
        
        <div className="flex justify-end space-x-4 gap-2">
          <button
            onClick={onClose}
            className="mr-2 px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded text-gray-800"
          >
            انصراف
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded text-white disabled:bg-green-400"
          >
            تایید درخواست
          </button>
        </div>
      </div>
    </div>
  );
};

// IncompleteTab component
const IncompleteTab: React.FC<{ onViewDetails: (requestId: string) => void; onAddressClick: (addressInfo: any) => void }> = ({ onViewDetails, onAddressClick }) => {
  const [requests, setRequests] = useState<TestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    total: 0,
    totalPages: 1
  });
  const itemsPerPage = 10;
  
  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR').format(date);
  };
  
  // Fetch incomplete requests
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const response = await getRequests({
          status: 'incomplete',
          page: pagination.currentPage,
          limit: itemsPerPage
        });
        
        if (response.success) {
          setRequests(response.data);
          setPagination({
            currentPage: response.pagination.currentPage,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          });
          setError('');
        } else {
          setError('خطا در دریافت درخواست‌ها');
        }
      } catch (err) {
        setError('خطا در ارتباط با سرور');

      } finally {
        setLoading(false);
      }
    };
    
    fetchRequests();
  }, [pagination.currentPage]);
  
  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  return (
    <div className="mt-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="overflow-x-auto">
        {loading ? (
          <div className="py-10 text-center text-gray-600">در حال بارگذاری...</div>
        ) : requests.length === 0 ? (
          <div className="py-10 text-center text-gray-600">هیچ درخواست ناقصی وجود ندارد</div>
        ) : (
          <>
            <table className="min-w-full bg-white divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="sticky-header sticky-col py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">شناسه درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">نام و نام خانوادگی</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ثبت درخواست</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ثبت درخواست</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ پاسخ آزمایشگاه</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت پاسخ آزمایشگاه</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ویرایش درخواست</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ویرایش درخواست</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">محل نمونه گیری</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ نمونه گیری</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت نمونه گیری</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">آخرین بروزرسانی</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">جزئیات</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(request => (
                  <tr key={request._id} className="border-b hover:bg-gray-50 group">
                    <td className="sticky-col py-3 px-4 text-sm shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">{request.displayId || request._id.slice(-8)}</td>
                    <td className="py-3 px-4 text-sm">{request.patientInfo?.firstName} {request.patientInfo?.lastName}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR').format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.samplingLocation?.type === 'home' ? (
                        <div className="text-xs">
                          <div>در منزل</div>
                          {request.addressInfo && (
                            <div className="mt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAddressClick(request.addressInfo);
                                }}
                                className="text-right p-2 bg-blue-50 hover:bg-blue-100 rounded text-xs text-blue-700 transition-colors w-full"
                              >
                                <div className="font-medium mb-1">آدرس: (کلیک برای مشاهده کامل)</div>
                                <div className="text-gray-600 truncate">
                                  {request.addressInfo.region && `منطقه: ${request.addressInfo.region}`}
                                </div>
                              </button>
                            </div>
                          )}
                        </div>
                      ) : 'در آزمایشگاه'}
                    </td>
                    <td className="py-3 px-4 text-sm">{request.samplingDate ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.samplingDate)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.samplingTime || '-'}</td>
                    <td className="py-3 px-4 text-sm">{formatDate(request.updatedAt)}</td>
                    <td className="py-3 px-4 text-sm"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusInfo(request.status, request).color}`}>{translateStatus(request.status, request)}</span></td>
                    <td className="py-3 px-4 text-sm"><button onClick={() => onViewDetails(request._id)} className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">جزئیات</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {pagination.totalPages > 1 && (
              <Pagination 
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// CanceledTab component
const CanceledTab: React.FC<{ onViewDetails: (requestId: string) => void; onAddressClick: (addressInfo: any) => void }> = ({ onViewDetails, onAddressClick }) => {
  const [requests, setRequests] = useState<TestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    total: 0,
    totalPages: 1
  });
  const itemsPerPage = 10;
  
  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR').format(date);
  };
  
  // Fetch canceled requests
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const response = await getRequests({
          status: 'canceled',
          page: pagination.currentPage,
          limit: itemsPerPage
        });
        
        if (response.success) {
          setRequests(response.data);
          setPagination({
            currentPage: response.pagination.currentPage,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          });
          setError('');
        } else {
          setError('خطا در دریافت درخواست‌ها');
        }
      } catch (err) {
        setError('خطا در ارتباط با سرور');

      } finally {
        setLoading(false);
      }
    };
    
    fetchRequests();
  }, [pagination.currentPage]);
  
  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  return (
    <div className="mt-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="overflow-x-auto">
        {loading ? (
          <div className="py-10 text-center text-gray-600">در حال بارگذاری...</div>
        ) : requests.length === 0 ? (
          <div className="py-10 text-center text-gray-600">هیچ درخواست لغو شده‌ای وجود ندارد</div>
        ) : (
          <>
            <table className="min-w-full bg-white divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="sticky-header sticky-col py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">شناسه درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">نام و نام خانوادگی</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ثبت درخواست</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ثبت درخواست</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ پاسخ آزمایشگاه</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت پاسخ آزمایشگاه</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ویرایش درخواست</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ویرایش درخواست</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">محل نمونه گیری</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ نمونه گیری</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت نمونه گیری</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ لغو</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">جزئیات</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(request => (
                  <tr key={request._id} className="border-b hover:bg-gray-50 group">
                    <td className="sticky-col py-3 px-4 text-sm shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">{request.displayId || request._id.slice(-8)}</td>
                    <td className="py-3 px-4 text-sm">{request.patientInfo?.firstName} {request.patientInfo?.lastName}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR').format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.samplingLocation?.type === 'home' ? (
                        <div className="text-xs">
                          <div>در منزل</div>
                          {request.addressInfo && (
                            <div className="mt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAddressClick(request.addressInfo);
                                }}
                                className="text-right p-2 bg-blue-50 hover:bg-blue-100 rounded text-xs text-blue-700 transition-colors w-full"
                              >
                                <div className="font-medium mb-1">آدرس: (کلیک برای مشاهده کامل)</div>
                                <div className="text-gray-600 truncate">
                                  {request.addressInfo.region && `منطقه: ${request.addressInfo.region}`}
                                </div>
                              </button>
                            </div>
                          )}
                        </div>
                      ) : 'در آزمایشگاه'}
                    </td>
                    <td className="py-3 px-4 text-sm">{request.samplingDate ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.samplingDate)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.samplingTime || '-'}</td>
                    <td className="py-3 px-4 text-sm">{formatDate(request.updatedAt)}</td>
                    <td className="py-3 px-4 text-sm"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusInfo(request.status, request).color}`}>{translateStatus(request.status, request)}</span></td>
                    <td className="py-3 px-4 text-sm"><button onClick={() => onViewDetails(request._id)} className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">جزئیات</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {pagination.totalPages > 1 && (
              <Pagination 
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// PaidTab component
const PaidTab: React.FC<{ onViewDetails: (requestId: string) => void; onAddressClick: (addressInfo: any) => void }> = ({ onViewDetails, onAddressClick }) => {
  const [requests, setRequests] = useState<TestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    total: 0,
    totalPages: 1
  });
  const itemsPerPage = 10;
  
  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR').format(date);
  };
  
  // Fetch paid requests
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const response = await getRequests({
          status: 'paid',
          page: pagination.currentPage,
          limit: itemsPerPage
        });
        
        if (response.success) {
          setRequests(response.data);
          setPagination({
            currentPage: response.pagination.currentPage,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          });
          setError('');
        } else {
          setError('خطا در دریافت درخواست‌ها');
        }
      } catch (err) {
        setError('خطا در ارتباط با سرور');

      } finally {
        setLoading(false);
      }
    };
    
    fetchRequests();
  }, [pagination.currentPage]);
  
  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  return (
    <div className="mt-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="overflow-x-auto">
        {loading ? (
          <div className="py-10 text-center text-gray-600">در حال بارگذاری...</div>
        ) : requests.length === 0 ? (
          <div className="py-10 text-center text-gray-600">هیچ درخواست پرداخت شده‌ای وجود ندارد</div>
        ) : (
          <>
            <table className="min-w-full bg-white divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="sticky-header sticky-col py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">شناسه درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">نام و نام خانوادگی</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ثبت درخواست</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ثبت درخواست</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ پاسخ آزمایشگاه</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت پاسخ آزمایشگاه</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ویرایش درخواست</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ویرایش درخواست</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">محل نمونه گیری</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ نمونه گیری</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت نمونه گیری</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ پرداخت</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">جزئیات</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(request => (
                  <tr key={request._id} className="border-b hover:bg-gray-50 group">
                    <td className="sticky-col py-3 px-4 text-sm shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">{request.displayId || request._id.slice(-8)}</td>
                    <td className="py-3 px-4 text-sm">{request.patientInfo?.firstName} {request.patientInfo?.lastName}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR').format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.samplingLocation?.type === 'home' ? (
                        <div className="text-xs">
                          <div>در منزل</div>
                          {request.addressInfo && (
                            <div className="mt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAddressClick(request.addressInfo);
                                }}
                                className="text-right p-2 bg-blue-50 hover:bg-blue-100 rounded text-xs text-blue-700 transition-colors w-full"
                              >
                                <div className="font-medium mb-1">آدرس: (کلیک برای مشاهده کامل)</div>
                                <div className="text-gray-600 truncate">
                                  {request.addressInfo.region && `منطقه: ${request.addressInfo.region}`}
                                </div>
                              </button>
                            </div>
                          )}
                        </div>
                      ) : 'در آزمایشگاه'}
                    </td>
                    <td className="py-3 px-4 text-sm">{request.samplingDate ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.samplingDate)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.samplingTime || '-'}</td>
                    <td className="py-3 px-4 text-sm">{formatDate(request.updatedAt)}</td>
                    <td className="py-3 px-4 text-sm"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusInfo(request.status, request).color}`}>{translateStatus(request.status, request)}</span></td>
                    <td className="py-3 px-4 text-sm"><button onClick={() => onViewDetails(request._id)} className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">جزئیات</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {pagination.totalPages > 1 && (
              <Pagination 
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ExpiredTab component
const ExpiredTab: React.FC<{ onViewDetails: (requestId: string) => void; onRefreshCounts?: () => void; onAddressClick: (addressInfo: any) => void }> = ({ onViewDetails, onRefreshCounts, onAddressClick }) => {
  const [requests, setRequests] = useState<TestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resettingDeadline, setResettingDeadline] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    total: 0,
    totalPages: 1
  });
  const itemsPerPage = 10;
  
  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR').format(date);
  };
  
  // Fetch expired requests
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const response = await getRequests({
          status: 'expired',
          page: pagination.currentPage,
          limit: itemsPerPage
        });
        
        if (response.success) {
          setRequests(response.data);
          setPagination({
            currentPage: response.pagination.currentPage,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          });
          setError('');
        } else {
          setError('خطا در دریافت درخواست‌ها');
        }
      } catch (err) {
        setError('خطا در ارتباط با سرور');

      } finally {
        setLoading(false);
      }
    };
    
    fetchRequests();
    }, [pagination.currentPage]);
  
  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  // Handle reset edit deadline for expired requests
  const handleResetEditDeadline = async (requestId: string) => {
    try {
      setResettingDeadline(requestId);
      const response = await resetEditDeadline(requestId);
      
      if (response.success) {
        // Remove the request from expired list since it's now approved
        setRequests(prevRequests => 
          prevRequests.filter(req => req._id !== requestId)
        );
        
        // Update pagination count
        setPagination(prev => ({
          ...prev,
          total: prev.total - 1
        }));
        
        // Refresh tab counts
        onRefreshCounts?.();
        
        toast.success(response.message || 'مهلت ویرایش با موفقیت بازنشانی شد و درخواست تایید شد');
      } else {
        toast.error(response.message || 'خطا در بازنشانی مهلت ویرایش');
      }
    } catch (error) {

      toast.error('خطا در بازنشانی مهلت ویرایش');
    } finally {
      setResettingDeadline(null);
    }
  };

  return (
    <div className="mt-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="overflow-x-auto border border-gray-200 rounded custom-scrollbar-ongoing" style={{ maxHeight: 'calc(100vh - 300px)' }}>
        <style>{`
          .custom-scrollbar-ongoing::-webkit-scrollbar {
            height: 10px;
            width: 10px;
          }
          .custom-scrollbar-ongoing::-webkit-scrollbar-track {
            background: #F3F4F6;
            border-radius: 4px;
          }
          .custom-scrollbar-ongoing::-webkit-scrollbar-thumb {
            background: #9CA3AF;
            border-radius: 4px;
          }
          .custom-scrollbar-ongoing::-webkit-scrollbar-thumb:hover {
            background: #6B7280;
          }
          .custom-scrollbar-ongoing {
            scrollbar-width: thin;
            scrollbar-color: #9CA3AF #F3F4F6;
          }
          /* Sticky header and first column */
          .sticky-header {
            position: sticky;
            top: 0;
            z-index: 10;
            background: #F9FAFB;
          }
          .sticky-col {
            position: sticky;
            right: 0;
            z-index: 5;
            background: white;
          }
          .sticky-header.sticky-col {
            z-index: 15;
            background: #F9FAFB;
          }
          tbody tr:hover .sticky-col {
            background: #F9FAFB;
          }
        `}</style>
        {loading ? (
          <div className="py-10 text-center text-gray-600">در حال بارگذاری...</div>
        ) : requests.length === 0 ? (
          <div className="py-10 text-center text-gray-600">هیچ درخواست منقضی شده‌ای وجود ندارد</div>
        ) : (
          <>
            <table className="min-w-full bg-white divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="sticky-header sticky-col py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">شناسه درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">نام و نام خانوادگی</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ثبت درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ثبت درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ پاسخ آزمایشگاه</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت پاسخ آزمایشگاه</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ویرایش درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ویرایش درخواست</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">محل نمونه گیری</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ نمونه گیری</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت نمونه گیری</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ انقضا</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">مهلت ویرایش</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت تمدید</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
                  <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">جزئیات</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(request => (
                  <tr key={request._id} className="border-b hover:bg-gray-50 group">
                    <td className="sticky-col py-3 px-4 text-sm shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">{request.displayId || request._id.slice(-8)}</td>
                    <td className="py-3 px-4 text-sm">{request.patientInfo?.firstName} {request.patientInfo?.lastName}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR').format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR', {hour:'2-digit', minute:'2-digit'}).format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.samplingLocation?.type === 'home' ? (
                        <div className="text-xs">
                          <div>در منزل</div>
                          {request.addressInfo && (
                            <div className="mt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAddressClick(request.addressInfo);
                                }}
                                className="text-right p-2 bg-blue-50 hover:bg-blue-100 rounded text-xs text-blue-700 transition-colors w-full"
                              >
                                <div className="font-medium mb-1">آدرس: (کلیک برای مشاهده کامل)</div>
                                <div className="text-gray-600 truncate">
                                  {request.addressInfo.region && `منطقه: ${request.addressInfo.region}`}
                                </div>
                              </button>
                            </div>
                          )}
                        </div>
                      ) : 'در آزمایشگاه'}
                    </td>
                    <td className="py-3 px-4 text-sm">{request.samplingDate ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.samplingDate)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.samplingTime || '-'}</td>
                    <td className="py-3 px-4 text-sm">{formatDate(request.updatedAt)}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.editDeadline ? (
                        <div className="text-xs">
                          <div className={`${isEditDeadlinePassed(request.editDeadline) ? 'text-red-600' : 'text-green-600'}`}>
                            {formatRemainingEditTime(request.editDeadline)}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-500">---</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusInfo(request.status, request).color}`}>{translateStatus(request.status, request)}</span></td>
                    <td className="py-3 px-4 text-sm"><button onClick={() => onViewDetails(request._id)} className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">جزئیات</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {pagination.totalPages > 1 && (
              <Pagination 
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OngoingRequests;

